import { useEffect, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';

import type {
  TestRealtimeModel,
  TestTranscriptionModel,
} from '@api-contracts/api';
import type { FallbackProfile, VoiceSettings } from '@api-contracts/voice';
import {
  CallActivityLog,
  CallLauncherCard,
  TestCallConfigCard,
  TestProfileSelect,
  type ActivityEntry,
  type ActivityParams,
} from '@components/agents';
import { Button } from '@components/ui/button';
import { Card } from '@components/ui/card';
import { Separator } from '@components/ui/separator';
import { queryKeys } from '@constants/queryKeys.constant';
import {
  defaultVoiceSettings,
  fallbackProfiles,
} from '@constants/voiceSettings.constant';
import { getErrorMessage } from '@helpers/getErrorMessage.helper';
import {
  useLiveKitRoom,
  type TranscriptionUpdate,
} from '@hooks/liveKitRoom.hook';
import { agentDetailQueryOptions } from '@services/agents/queryOptions';
import { testCallOptionsQueryOptions } from '@services/voice/queryOptions';

export const Route = createFileRoute('/_dashboard/agents/$agentId/test')({
  component: AgentTestPage,
  errorComponent: AgentTestError,
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        agentDetailQueryOptions(params.agentId),
      ),
      context.queryClient.ensureQueryData(testCallOptionsQueryOptions()),
    ]),
});

function AgentTestError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  function handleRetry() {
    reset();
    void router.invalidate();
  }

  return (
    <section className="grid min-h-full place-items-center p-6">
      <Card className="grid max-w-xl gap-4 text-center">
        <div>
          <h1 className="text-primary-dark text-2xl font-extrabold">
            Couldn&apos;t load the test page
          </h1>
          <p className="text-muted mt-2 text-sm leading-6">
            {getErrorMessage(error)}
          </p>
        </div>
        <div className="flex justify-center gap-3">
          <Button asChild textTransform="none" variant="secondary">
            <Link to="/agents">Back to agents</Link>
          </Button>
          <Button
            onClick={handleRetry}
            textTransform="none"
            type="button"
            variant="primary"
          >
            Try again
          </Button>
        </div>
      </Card>
    </section>
  );
}

const initialLiveActivity: Array<ActivityEntry> = [
  { id: 0, message: 'Ready.', tone: 'info' },
];

function AgentTestPage() {
  const { agentId } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: agent } = useQuery(agentDetailQueryOptions(agentId));
  const { data: testCallOptions } = useQuery(testCallOptionsQueryOptions());

  const [liveActivity, setLiveActivity] =
    useState<Array<ActivityEntry>>(initialLiveActivity);
  const nextActivityId = useRef(1);
  const transcriptionEntryIds = useRef(new Map<string, number>());
  const [selectedProfile, setSelectedProfile] = useState<FallbackProfile>(
    fallbackProfiles[0],
  );
  const [isTestPending, setIsTestPending] = useState(false);
  const [model, setModel] = useState<TestRealtimeModel>(
    () => testCallOptions?.defaults.realtimeModel ?? 'gpt-realtime-2',
  );
  const [transcriptionModel, setTranscriptionModel] =
    useState<TestTranscriptionModel>(
      () => testCallOptions?.defaults.transcriptionModel ?? 'gpt-4o-transcribe',
    );
  const [voiceSettings, setVoiceSettings] = useState<Required<VoiceSettings>>(
    () => agent?.voiceSettings ?? defaultVoiceSettings,
  );
  function handleTranscription({
    isFinal,
    segmentId,
    speaker,
    text,
  }: TranscriptionUpdate) {
    const message = `${speaker}: ${text}`;
    const existingId = transcriptionEntryIds.current.get(segmentId);

    if (existingId !== undefined) {
      setLiveActivity(current =>
        current.map(entry =>
          entry.id === existingId ? { ...entry, message } : entry,
        ),
      );
      return;
    }

    const id = nextActivityId.current;
    nextActivityId.current += 1;
    transcriptionEntryIds.current.set(segmentId, id);

    setLiveActivity(current => [
      { id, message, tone: isFinal ? 'success' : 'info' },
      ...current,
    ]);
  }

  const liveKitRoom = useLiveKitRoom({ onTranscription: handleTranscription });

  function handleSessionStart() {
    setLiveActivity(initialLiveActivity);
    transcriptionEntryIds.current.clear();
    nextActivityId.current = 1;
  }

  function handleModelChange(nextModel: TestRealtimeModel) {
    setModel(nextModel);

    const defaultTranscriptionModel = testCallOptions?.realtimeModels.find(
      realtimeModel => realtimeModel.id === nextModel,
    )?.defaultTranscriptionModel;

    if (defaultTranscriptionModel) {
      setTranscriptionModel(defaultTranscriptionModel);
    }
  }

  useEffect(() => {
    return () => {
      void liveKitRoom.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pushActivity({ message, tone }: ActivityParams) {
    const id = nextActivityId.current;
    nextActivityId.current += 1;

    setLiveActivity(current => [{ id, message, tone }, ...current]);
  }

  function handleSessionEnd() {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.agents.calls(agentId),
    });
  }

  if (!agent || !testCallOptions) {
    return null;
  }

  return (
    <section className="grid min-h-full gap-10" data-slot="agent-test-page">
      <header className="min-w-0">
        <p className="eyebrow">LiveKit voice agent</p>
        <h1 className="text-primary-dark text-4xl leading-none font-extrabold md:text-6xl">
          {agent.name}
        </h1>
        <p className="subhead">Test this agent with a browser voice session.</p>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[400px_minmax(0,1fr)]">
        <Card className="grid gap-6 p-6 sm:p-8" data-slot="test-configuration">
          <TestProfileSelect
            onProfileChange={setSelectedProfile}
            profiles={fallbackProfiles}
            selectedProfile={selectedProfile}
          />

          <Separator className="my-0" variant="form" />

          <CallLauncherCard
            agentId={agentId}
            liveKitRoom={liveKitRoom}
            model={model}
            onActivity={pushActivity}
            onPendingChange={setIsTestPending}
            onSessionEnd={handleSessionEnd}
            onSessionStart={handleSessionStart}
            selectedProfile={selectedProfile}
            testDraft
            transcriptionModel={transcriptionModel}
            voiceSettings={voiceSettings}
          />

          <Separator className="my-0" variant="form" />

          <TestCallConfigCard
            disabled={isTestPending || liveKitRoom.status === 'connecting'}
            onModelChange={handleModelChange}
            onTranscriptionModelChange={setTranscriptionModel}
            onVoiceSettingsChange={setVoiceSettings}
            options={testCallOptions}
            selectedModel={model}
            selectedTranscriptionModel={transcriptionModel}
            voiceSettings={voiceSettings}
          />
        </Card>

        <CallActivityLog
          entries={liveActivity}
          isMicMuted={liveKitRoom.isMicMuted}
          liveStatus={liveKitRoom.status}
        />
      </div>
    </section>
  );
}
